import { isSuccess } from '@orus.eu/result'
import { getMissingPermissions, type AnyOfPermission, type Permission } from '@orus.eu/right-access-management'

import { createLoginRedirect } from './build-login-href.ts'
import { resetLoginBounce } from './detect-login-redirect-loop.ts'
import type { SessionGuardOptions } from './session-access.ts'

/**
 * Page-level access guard, called inside a route `beforeLoad`. Reads the session out of the router context,
 * and throws a redirect to the login page when the user is anonymous or lacks a required permission.
 *
 * Note: logged-in-but-forbidden currently redirects to login. A 403-in-place via errorComponent is a deliberate
 * follow-up (cf. routing-frontend.md, Décision 8).
 */
export async function requirePermissions(
  { context, location }: SessionGuardOptions,
  requiredPermissions: (Permission | AnyOfPermission)[],
): Promise<void> {
  const sessionResult = await context.session.getSessionResult()

  // Session unavailable (client outdated): handled elsewhere by SessionManager — do not block the route.
  if (!isSuccess(sessionResult)) return

  const session = sessionResult.output

  if (!session.user) {
    throw createLoginRedirect(location.href)
  }

  const missingPermissions = getMissingPermissions(session.allPermissions, requiredPermissions)
  if (missingPermissions.length > 0) {
    throw createLoginRedirect(location.href)
  }

  // Reached an authorized page: any in-flight bounce episode ended without looping
  resetLoginBounce()
}
